import { IProductVariationAsset } from 'src/components/productVariationTable/ProductVariationAssets.interface';
import type {
  ComponentLineupStylesOverride,
  GcfPriceDisplayFormat,
  SubHeaderConfig,
} from 'src/features/components/editComponentPage/forms/FormSelector.interface';
import type { OptionalVisualInterface } from 'src/features/components/editComponentPage/forms/listingWithOptionalVisuals/ListingWithOptionalVisuals.interface';

import type { ICCAsset } from './Assets';
import {
  IProductThemeType,
  ISubproductThemeType,
  IThemeRes,
} from './GCFThemes';
import { ITemplateVariation } from './TemplateVariations';

/*
  GCF interfaces
*/
export interface IGcfProductList {
  config: IGcfProductListConfig;
  groups: IGcfProductListGroups;
}

export interface IGcfProductListConfig {
  multilineSizes: boolean;
  groupHeader?: {
    styles: SubHeaderConfig;
  };
  products?: {
    styles?: Partial<IProductThemeType>;
  };
  sizes?: ISizesStyle;
  sizeLabel?: {
    type: GcfPriceLabelTypeEnum;
    shape: GcfPriceLabelShapeTypeEnum;
  };
  subProductsDisplayFormat?: {
    type: GcfPriceDisplayFormat;
  };
  badges?: {
    visible: boolean;
    groupBadge?: IGcfBadgeConfig;
    itemBadge?: IGcfBadgeConfig;
    imageBadge?: IGcfBadge;
  };
  visuals?: OptionalVisualInterface;
}

export interface ISizesStyle {
  defaultConfig: ISizesConfig;

  [sizeName: string]: ISizesConfig;
}

export interface SizesConfigName {
  [key: string | number]: ISizesConfig;
}

export interface ISizesConfig {
  displayLabel?: boolean;
  styles?: ISubproductThemeType;
  nameOverride?: string;
}

export interface IGcfBadgeConfig extends IGcfCommonPositionConfig {
  startText: string;

  startFrom?: string;
  style?: IGcfCommonStyle;
}

export interface IGcfProductListGroups {
  [order: string]: IGcfProductListGroup;
}

export interface IGcfProductListGroup {
  order: number;
  config: IGcfProductListGroupConfig;
  backups: IGcfProductListProducts;
  products: IGcfProductListProducts;
}

export interface IGcfProductListGroupConfig {
  column?: number; // <=== Combo column property
  columnOrder?: number; // <=== LVG column
  groupHeader?: IGroupHeaderConfig;
  groupDisclaimer?: IGroupDisclaimerConfig;

  displayOrder?: boolean;
  offsetTop?: string;
  productOrderOverride?: string;
}

export interface IGroupDescriptionConfig extends IGcfCommonPositionConfig {
  text: string;

  font?: string;
  color?: string;
  fontSize?: string;
  lineHeight?: string;
  height?: number;
  marginBottom?: string;
  marginLeft?: string;
}

export interface IGroupHeaderConfig extends IGcfCommonPositionConfig {
  text: string;

  height?: number;
  font?: string;
  fontSize?: string;
  lineHeight?: string;
  color?: string;
  marginBottom?: string;
  marginLeft?: string;
  border?: string;
  borderRadius?: string;
  backgroundColor?: string;
  alignment?: string;
  description?: IGroupHeaderConfig;
  pill?: {
    visible: boolean;
  };
}

export interface IGroupDisclaimerConfig extends IGcfCommonPositionConfig {
  text: string;
  fontSize?: string;
  lineHeight?: string;
  color?: string;
  alignment?: string;
  marginBottom?: string;
}

export interface IGcfProductListProducts {
  [order: string]: IGcfProductListProduct;
}

export interface IGcfProductListProduct {
  uid?: string;
  order: number;
  productCode: string;
  config: IGcfProductListProductConfig;
  sizes: IGcfProductListProductSizes;
  bundleImages?: IGcfProductListProductImage[];
}

export interface IGcfProductListProductConfig {
  defaultSize?: string;
  nameOverride: string;
  animation?: IAnimation;
  subproductsDisplayFormat?: SUBPRODUCT_PRICE_DISPLAY_FORMAT;
  displayProductDisclaimer?: boolean;
  hideDisplayName?: boolean;

  /**
   * @deprecated
   */
  stylesOverwrite?: {
    subproducts?: ComponentLineupStylesOverride;
  };

  positionLeft?: number;
  positionTop?: number;
  enabledAnimationTypeKey?: AnimationTypeKeys;

  styles?: IGcfCommonStyle;
  sizes?: ISizesStyle;

  // Internal use only
  isBackupProduct?: boolean;
}

/**
 * @deprecated This interface is deprecated. Since R13
 */
export interface IGcfOutageStampConfig {
  visible: boolean;
  url?: string;
  width?: string;
  height?: string;
  left?: string;
  top?: string;
}

export interface IGcfProductListProductImage {
  visible?: boolean;
  assetName?: string;
  style?: IGcfCommonStyle;
  badge?: IGcfBadge;
  outageStamp?: IGcfOutageStamp;
  productCode?: string;
}

export interface IGcfBadge {
  visible: boolean;
  style?: IGcfCommonStyle;
}

export interface IGcfOutageStamp {
  visible: boolean;
  url?: string;
  style?: IGcfCommonStyle;
}

export interface IGcfProductListProductSizes {
  [sizeName: string]: IGcfProductListProductSize;
}

export interface IGcfProductListProductSize {
  order: number;
  name?: string;
  config: {
    showLabel: boolean;
    showPrice: boolean;
    showCalories: boolean;
    showFlavors: boolean;
  };
  variations: IGcfProductListProductSizeVariations;
}

export interface IGcfProductListProductSizeVariations {
  [productCode: string]: IGcfProductListProductSizeVariation;
}

export interface IGcfProductListProductSizeVariation {
  order: number;
  productCode: string;
  config: {
    images?: IGcfProductListProductImage[];
  };
}

export interface IGcfCommonPositionConfig {
  type: string;
  visible: boolean;
  background?: {
    color?: string;
    image?: string;
  };
  position?: {
    top?: string | number;
    left?: string | number;
    width?: string | number;
    height?: string | number;
  };
}

export interface IGcfCommonStyle {
  css?: string;
  font?: string;
  fontSize?: string;
  color?: string;
  size?: string;
  backgroundColor?: string;
  top?: string;
  left?: string;
  width?: string;
  height?: string;
  zIndex?: number;
  lineHeight?: string;
  badgeSize?: string;
  fontColor?: string;
  margin?: string;
}

export enum GcfPriceLabelTypeEnum {
  'None' = 'NONE',
  'Acronym' = 'shortCode',
  'Size' = 'size',
}

export enum GcfPriceLabelShapeTypeEnum {
  'Pill' = 'PILL',
  'None' = 'NONE',
}

export enum TextAlignmentEnum {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export enum GcfGroupProductType {
  PRODUCTS = 'products',
  BACKUPS = 'backups',
}

export enum SUBPRODUCT_PRICE_DISPLAY_FORMAT { // Can be used at lineup level as it has NONE
  INLINE = 'INLINE',
  MULTILINE = 'MULTILINE',
  NONE = 'NONE',
}

export enum AnimationTypes {
  FADE = 'fade',
  APPEAR = 'appear',
}

export enum AnimationTypeKeys {
  OFF = 'Off',
  MOLECULE = 'Molecule',
  ELEMENTS = 'Elements',
}

export interface IAnimation extends IAnimationBase {
  productName: IAnimationBase | null;
  label: IAnimationBase | null;
  price: IAnimationBase | null;
  nutrition: IAnimationBase | null;
  disclaimer: IAnimationBase | null;
}

enum AnimationStatus {
  IN = 'in',
  OUT = 'out',
}

interface IAnimationConfig {
  delay: number;
  type?: AnimationTypes;
  duration?: number;
}

interface IAnimationBase {
  [AnimationStatus.IN]: IAnimationConfig | null;
  [AnimationStatus.OUT]: IAnimationConfig | null;
}

/*
  CC-BE interfaces
*/
export interface IProduct {
  product_code: string;
  name_override: string | null;
  product_name: string;
  active: boolean;
  product_category_id: number[];
  product_category_names?: string[];
  created_at: string;
  modified_at: string;
  productVariations: IProductVariation[];
  productCategories: IProductCategory[];
  asset_id: number[];
  asset_name?: string[];
  hqasset_name?: string[];
  assets?: ICCAsset[];
  themes?: IThemeRes[];
  has_assigned_component?: string;
  narrative?: string;
}

export interface IProductVariation {
  product_code: string;
  group_product_code: string;
  product_variation_size_id: number;
  product_variation_flavor_id: number;
  name: string;
  variationAssets?: IProductVariationAsset[];
  description: string;
  disclaimer: string;
  volume: number;
  legal: string;
  nutrition: string;
  active: boolean;
  productVariationSize?: IProductVariationSize;
  productVariationFlavor?: IProductVariationFlavor;
  productVariantName?: string;
  productFlavorName?: string;
  created_at: string;
  modified_at: string;
  asset_id: number[];
  assets?: ICCAsset[];
  has_assigned_component?: string;
  dy_asset?: number | null;
  variation_template_id: number[];
  variation_template?: {
    id: number;
    template_id: number;
    template_name: string;
    variations: ITemplateVariation[];
  } | null;
}

export interface IProductVariationSize {
  id: number;
  name: string;
  short: string;
  created_at: string;
  modified_at: string;
}

export interface IProductVariationFlavor {
  id: number;
  name: string;
  created_at: string;
  modified_at: string;
}

export interface IProductCategory {
  id: number;
  name: string;
  created_at: string;
  modified_at: string;
}

export interface IProductCategoriesWithProducts {
  id: number;
  name: string;
  products_count: number;
  created_at: string;
  modified_at: string;
}

export interface IVariationAsset {
  id: number;
  name: string;
  theme_id: number[];
  in_use?: boolean;
  created_at: string;
  modified_at: string;
}

export interface IImageTheme {
  id: number;
  name: string;
  created_at?: Date;
  modified_at?: Date;
  config?: any;
}

export interface IVariationTemplate {
  id: number;
  product_code: string;
  template_id: number;
  variation_template_variation_ids: number[];
  config?: any;
}

export interface ITemplate {
  id: number;
  name: string;
  description: string;
  config: any;
}

export interface IVariationTemplateVariation {
  id: number;
  order: number;
  product_code: string;
  variation_template_id: number;
  config?: any;
}
